import { useState } from 'react';
import { api } from '../lib/api';
import { startSsoLogin, setSessionToken, type AuthConfig } from '../lib/auth';

/**
 * Sign-in screen (entra mode). Offers Microsoft SSO when configured, plus the
 * email magic-link fallback — the path to use when SSO is unavailable or a user
 * can't reach Microsoft sign-in. Invite-only: the link is only delivered if the
 * address already has a Tracx account, and the response never reveals which.
 */
export function LoginPage({ cfg, initialError }: { cfg: AuthConfig; initialError?: string | null }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(initialError ?? null);
  // Break-glass: under SSO-only the email form is hidden, but a super admin can
  // reveal it to recover a broken Entra config. The server only delivers a link
  // to an owner, so exposing the form to anyone is harmless.
  const [recovery, setRecovery] = useState(false);
  const showEmailForm = cfg.emailLoginEnabled || recovery;

  // Emergency username/password sign-in (enabled by an admin during an SSO/email
  // outage). Mints a normal session, so the app treats it like any other login.
  const [pwEmail, setPwEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  async function passwordLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!pwEmail.trim() || !password) return;
    setPwBusy(true);
    setError(null);
    try {
      const res = await api.post<{ token: string }>('/v2/auth/password/login', {
        email: pwEmail.trim(),
        password,
      });
      setSessionToken(res.token);
      window.location.assign('/'); // reload clean at root with the session set
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
      setPwBusy(false);
    }
  }

  async function requestLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError(null);
    try {
      const res = await api.post<{ message: string }>('/v2/auth/email/request', { email: email.trim() });
      setSent(true);
      setError(res.message ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the sign-in link.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-surface">
      <div className="card w-full max-w-sm p-6 space-y-5">
        <div className="text-center space-y-1">
          <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand text-brand-ink font-semibold">
            A
          </div>
          <h1 className="text-lg font-semibold">Sign in to Tracx</h1>
          <p className="text-xs text-muted">Atlas Next</p>
        </div>

        {error && (
          <div className={`text-sm rounded-lg px-3 py-2 ${sent ? 'bg-surface2 text-muted' : 'bg-rose-500/10 text-rose-600'}`}>
            {error}
          </div>
        )}

        {cfg.sso && (
          <>
            <button className="btn-brand w-full justify-center" onClick={() => startSsoLogin(cfg.sso!.loginUrl)}>
              Sign in with Microsoft
            </button>
            <div className="flex items-center gap-3 text-xs text-muted">
              <span className="h-px flex-1 bg-border" />
              or
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        {cfg.passwordLoginEnabled && (
          <form onSubmit={passwordLogin} className="space-y-2">
            <label className="text-sm text-muted">Sign in with your email and password</label>
            <input
              type="email"
              className="input w-full"
              placeholder="you@tracxsystems.com"
              value={pwEmail}
              onChange={(e) => setPwEmail(e.target.value)}
              autoComplete="username"
            />
            <input
              type="password"
              className="input w-full"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <button className="btn-brand w-full justify-center" disabled={pwBusy}>
              {pwBusy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {cfg.passwordLoginEnabled && showEmailForm && (
          <div className="flex items-center gap-3 text-xs text-muted">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>
        )}

        {showEmailForm &&
          (sent ? (
            <p className="text-sm text-muted text-center">
              Check your inbox for a sign-in link. You can close this tab.
            </p>
          ) : (
            <form onSubmit={requestLink} className="space-y-2">
              <label className="text-sm text-muted">
                {!cfg.emailLoginEnabled
                  ? 'Administrator recovery — email a sign-in link'
                  : cfg.sso
                    ? 'Email a sign-in link instead'
                    : 'Email a sign-in link'}
              </label>
              <input
                type="email"
                className="input w-full"
                placeholder="you@tracxsystems.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <button className="btn-ghost w-full justify-center border border-border" disabled={sending}>
                {sending ? 'Sending…' : 'Email me a sign-in link'}
              </button>
            </form>
          ))}

        {cfg.enforceSso && (
          <div className="text-center space-y-1">
            <p className="text-xs text-muted">This workspace requires Microsoft sign-in.</p>
            {!showEmailForm && !sent && (
              <button
                type="button"
                className="text-[11px] text-muted underline underline-offset-2 hover:text-fg"
                onClick={() => setRecovery(true)}
              >
                Administrator recovery
              </button>
            )}
          </div>
        )}

        <p className="text-[11px] text-muted text-center">
          Access is invite-only. Contact an administrator if you can't sign in.
        </p>
      </div>
    </div>
  );
}
